import { apiClient, ApiError, getAuthToken } from '@/shared/data/client';
import {
  mapOrganisationDto,
  mapOrganisationPlanUpgradePreviewDto,
  mapQueryResult,
} from '@/shared/data/dtoMappers';
import { getErrorMessage } from '@/shared/data/errors';
import type {
  OrganisationBillingPlanChangeDto,
  OrganisationCheckoutSessionDto,
  OrganisationDto,
  OrganisationPlanUpgradePreviewDto,
  QueryFilter,
  QueryResult,
  QuerySort,
  ReleaseStatus,
  PlanCode,
} from '@flowqa/shared';

const toApiError = (err: unknown): ApiError =>
  err instanceof ApiError ? err : new ApiError(getErrorMessage(err));

const ensureToken = (token?: string | null): string => {
  const resolved = token ?? getAuthToken();
  if (!resolved) {
    throw new ApiError('Not authenticated');
  }
  return resolved;
};

export const queryOrganisations = async (): Promise<QueryResult<OrganisationDto>> => {
  const token = ensureToken();
  try {
    const result = await apiClient.post<QueryResult<OrganisationDto>>(
      'organisations/query',
      { page: 1, pageSize: 100, sorts: [{ field: 'name', direction: 'asc' }] },
      {
        token,
        retries: 1,
        // Ensure org list is global; omit any org header to avoid scoping errors.
        orgId: null,
      },
    );
    return mapQueryResult(result, mapOrganisationDto);
  } catch (err) {
    throw toApiError(err);
  }
};

export const queryOrganisationsGrid = async (input: {
  page: number;
  pageSize: number;
  sorts: QuerySort[];
  filters: QueryFilter[];
}): Promise<QueryResult<OrganisationDto>> => {
  const token = ensureToken();
  try {
    const result = await apiClient.post<QueryResult<OrganisationDto>>(
      'organisations/query',
      { page: input.page, pageSize: input.pageSize, sorts: input.sorts, filters: input.filters },
      { token, retries: 1, orgId: null },
    );
    return mapQueryResult(result, mapOrganisationDto);
  } catch (err) {
    throw toApiError(err);
  }
};

export const createOrganisation = async (input: { name: string }): Promise<OrganisationDto> => {
  const token = ensureToken();
  try {
    const result = await apiClient.post<OrganisationDto>(
      'organisations',
      { name: input.name.trim() },
      { token, retries: 1 },
    );
    return mapOrganisationDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export const getOrganisationById = async (id: string): Promise<OrganisationDto> => {
  const token = ensureToken();
  try {
    const result = await apiClient.get<OrganisationDto>(`organisations/${id}`, {
      token,
      retries: 1,
    });
    return mapOrganisationDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export const updateOrganisationSettings = async (input: {
  id: string;
  automateReleaseStatuses: boolean;
  requireFeaturesForReady: boolean;
  requireReadyFeaturesForReady: boolean;
  recomputeReleaseStatuses?: boolean;
  recomputeReleaseIds?: string[];
}): Promise<OrganisationDto> => {
  const token = ensureToken();
  try {
    const result = await apiClient.patch<OrganisationDto>(
      `organisations/${input.id}/settings`,
      {
        automateReleaseStatuses: input.automateReleaseStatuses,
        requireFeaturesForReady: input.requireFeaturesForReady,
        requireReadyFeaturesForReady: input.requireReadyFeaturesForReady,
        recomputeReleaseStatuses: input.recomputeReleaseStatuses,
        recomputeReleaseIds: input.recomputeReleaseIds,
      },
      { token, retries: 1 },
    );
    return mapOrganisationDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export const upgradeOrganisation = async (input: {
  id: string;
  name: string;
  planCode: PlanCode;
  addOns?: { deliveryEnabled?: boolean; operationsEnabled?: boolean; codeEnabled?: boolean };
}): Promise<OrganisationDto> => {
  const token = ensureToken();
  try {
    const result = await apiClient.post<OrganisationDto>(
      `organisations/${input.id}/upgrade`,
      {
        name: input.name.trim(),
        planCode: input.planCode,
        addOns: input.addOns,
      },
      { token, retries: 1 },
    );
    return mapOrganisationDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export const previewOrganisationUpgrade = async (input: {
  id: string;
  planCode: PlanCode;
}): Promise<OrganisationPlanUpgradePreviewDto> => {
  const token = ensureToken();
  try {
    const result = await apiClient.post<OrganisationPlanUpgradePreviewDto>(
      `organisations/${input.id}/upgrade/preview`,
      {
        planCode: input.planCode,
      },
      { token, retries: 1 },
    );
    return mapOrganisationPlanUpgradePreviewDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export const createOrganisationCheckoutSession = async (input: {
  id: string;
  planCode: PlanCode;
  successUrl: string;
  cancelUrl: string;
}): Promise<OrganisationCheckoutSessionDto> => {
  const token = ensureToken();
  try {
    return await apiClient.post<OrganisationCheckoutSessionDto>(
      `organisations/${input.id}/billing/checkout-session`,
      {
        planCode: input.planCode,
        successUrl: input.successUrl,
        cancelUrl: input.cancelUrl,
      },
      { token, retries: 1 },
    );
  } catch (err) {
    throw toApiError(err);
  }
};

export const changeOrganisationBillingPlan = async (input: {
  id: string;
  planCode: PlanCode;
  prorationDate?: number | null;
}): Promise<OrganisationBillingPlanChangeDto> => {
  const token = ensureToken();
  try {
    const result = await apiClient.post<OrganisationBillingPlanChangeDto>(
      `organisations/${input.id}/billing/change-plan`,
      {
        planCode: input.planCode,
        prorationDate: input.prorationDate ?? null,
      },
      { token, retries: 1 },
    );
    return {
      organisation: mapOrganisationDto(result.organisation),
    };
  } catch (err) {
    throw toApiError(err);
  }
};

export const cancelOrganisationBillingRenewal = async (input: {
  id: string;
}): Promise<OrganisationBillingPlanChangeDto> => {
  const token = ensureToken();
  try {
    const result = await apiClient.post<OrganisationBillingPlanChangeDto>(
      `organisations/${input.id}/billing/cancel-renewal`,
      {},
      { token, retries: 1 },
    );
    return {
      organisation: mapOrganisationDto(result.organisation),
    };
  } catch (err) {
    throw toApiError(err);
  }
};

export const updateOrganisation = async (input: {
  id: string;
  name: string;
}): Promise<OrganisationDto> => {
  const token = ensureToken();
  try {
    const result = await apiClient.patch<OrganisationDto>(
      `organisations/${input.id}`,
      { name: input.name.trim() },
      { token, retries: 1 },
    );
    return mapOrganisationDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export const deleteOrganisation = async (input: { id: string }): Promise<{ status: string }> => {
  const token = ensureToken();
  try {
    return await apiClient.post<{ status: string }>(`organisations/${input.id}/delete`, undefined, {
      token,
      retries: 1,
    });
  } catch (err) {
    throw toApiError(err);
  }
};

export type ReleaseAutomationPreviewItem = {
  id: string;
  slug: string;
  name: string;
  projectId: string;
  projectName: string;
  currentStatus: ReleaseStatus;
  nextStatus: ReleaseStatus;
};

export const previewReleaseAutomationChanges = async (input: {
  id: string;
  automateReleaseStatuses: boolean;
  requireFeaturesForReady: boolean;
  requireReadyFeaturesForReady: boolean;
}): Promise<{ items: ReleaseAutomationPreviewItem[] }> => {
  const token = ensureToken();
  try {
    return await apiClient.post<{ items: ReleaseAutomationPreviewItem[] }>(
      `organisations/${input.id}/release-automation/preview`,
      {
        automateReleaseStatuses: input.automateReleaseStatuses,
        requireFeaturesForReady: input.requireFeaturesForReady,
        requireReadyFeaturesForReady: input.requireReadyFeaturesForReady,
      },
      { token, retries: 1 },
    );
  } catch (err) {
    throw toApiError(err);
  }
};
